import { Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FormsService } from '../../shared/services/forms.service';
import { Field } from '../interfaces/hubspot-form-data';
import { FormUtilsService } from './form-utils.service';
import { ValidatorUtils } from './validator-utils';

@Injectable()
export class FormBuilderService {
    constructor(private _formUtilsService: FormUtilsService, private _formsService: FormsService) {
    }

    public getReactiveFormFromSimpleSchema(flatData: any, guid?: string): UntypedFormGroup {
        const formControls = {};
        flatData.forEach((field: any) => {
            if (this.getFormControlByFieldType(field)) {
                formControls[field.name] = this.getFormControlByFieldType(field);
            }
        });

        return new UntypedFormGroup(formControls);
    }

    public getFormControlByFieldType(field: Field, value = null): UntypedFormControl {
        if (field.fieldType.toLowerCase() === 'radio') {
            return this._addRadiobutton(field, value);
        } else if (field.fieldType.toLowerCase() === 'checkbox' || field.fieldType.toLowerCase() === 'booleancheckbox') {
            return this._addCheckbox(field, value);
        } else if (field.fieldType.toLowerCase() === 'modalcheckbox') {
            return this._addCheckboxWithModal(field);
        } else if (field.fieldType.toLowerCase() === 'textarea') {
            return this._addTextarea(field, value);
        } else if (field.fieldType.toLowerCase() === 'date') {
            return this._addDateField(field, value);
        } else if (field.fieldType.toLowerCase() === 'select') {
            return this._addSelectField(field, value);
        } else if (field.fieldType.toLowerCase() === 'number') {
            return this._addNumberField(field, value);
        } else if (field.fieldType.toLowerCase() === 'phonenumber') {
            return this._addPhoneField(field, value);
        } else if (field.fieldType.toLowerCase() === 'text') {
            if (field.name.toLowerCase() === 'email') {
                return this._addEmailField(field, value);
            } else if (field.name.toLowerCase().indexOf('phone') > -1) {
                return this._addPhoneField(field, value);
            } else {
                return this._addTextField(field, value);
            }
        } else {
            throw new Error('No function for fieldType ' + field.fieldType.toLowerCase());
        }
    }

    private _addTextField(field: Field, value = null) {
        const _value =
            field.name === 'sp_last_form_submission_url'
                ? window.location.href
                : value
                ? value
                : field.defaultValue
                    ? field.defaultValue
                    : null;
        return new UntypedFormControl(
            {
                value: _value,
                disabled: !field.enabled
            },
            ValidatorUtils.TEXTFIELD_VALIDATORS(field)
        );
    }

    private _addEmailField(field: Field, value = null) {
        return new UntypedFormControl(
            {value: value || field.defaultValue || '', disabled: !field.enabled},
            ValidatorUtils.EMAIL_VALIDATORS(field, this._formUtilsService)
        );
    }

    private _addDateField(field: Field, value = null) {
        return new UntypedFormControl(
            {
                value: value || field.defaultValue || '',
                disabled: !field.enabled
            },
            ValidatorUtils.DATE_VALIDATORS(field)
        );
    }

    private _addNumberField(field: Field, value = null) {
        return new UntypedFormControl(
            {
                value: value || field.defaultValue || '',
                disabled: !field.enabled
            },
            ValidatorUtils.NUMBER_VALIDATORS(field)
        );
    }

    private _addPhoneField(field: Field, value = null) {
        return new UntypedFormControl(
            {
                value: value || field.defaultValue || '',
                disabled: !field.enabled
            },
            ValidatorUtils.PHONE_VALIDATORS(field)
        );
    }

    private _addSelectField(field: Field, value = null) {
        const preselection = value ? value : field.selectedOptions && field.selectedOptions[0] ? field.selectedOptions[0] : null;
        return new UntypedFormControl(
            {
                value: preselection ? preselection : '',
                disabled: !field.enabled
            },
            ValidatorUtils.SELECT_VALIDATORS(field)
        );
    }

    private _addTextarea(field: Field, value = null) {
        return new UntypedFormControl(
            {
                value: value || field.defaultValue || '',
                disabled: !field.enabled
            },
            ValidatorUtils.TEXTAREA_VALIDATORS(field)
        );
    }

    private _addCheckbox(field: Field, value = null) {
        if (value && field.fieldType === 'checkbox') {
            let _preselected = '';

            value.forEach((val) => {
                _preselected += val.value + ',';
            });
            return new UntypedFormControl({
                value: _preselected,
                disabled: !field.enabled
            });
        } else {
            return new UntypedFormControl(
                {
                    value: value || field.defaultValue || '',
                    disabled: !field.enabled
                },
                ValidatorUtils.CHECKBOX_VALIDATORS(field)
            );
        }
    }

    private _addCheckboxWithModal(field: Field) {
        return new UntypedFormControl(
            {
                value: field.defaultValue || '',
                disabled: !field.enabled
            },
            ValidatorUtils.CHECKBOX_VALIDATORS(field)
        );
    }

    private _addRadiobutton(field: Field, value = null) {
        return new UntypedFormControl(
            {
                value: value || field.defaultValue || '',
                disabled: !field.enabled
            },
            ValidatorUtils.RADIO_VALIDATORS(field)
        );
    }
}
